import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Empty, Select, Space, Spin, Tag, Typography } from 'antd';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuthSession } from '@/entities/auth/api/hooks';
import { useEmployees, useEmployee } from '@/entities/employee/api/hooks';
import { usePositions } from '@/entities/position/api/hooks';
import { OrgStructureSelectDrawer } from '@/shared/ui/OrgStructureSelectDrawer';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { SearchInput } from '@/shared/ui/SearchInput';
import { EmployeeCard } from './EmployeeCard';
import { EmployeeRow } from './EmployeeRow';
import { OrgTreeDrawer } from './OrgTreeDrawer';
import { ApartmentOutlined } from '@ant-design/icons';

import s from './Employees.module.scss';

const { Text } = Typography;

export function Employees() {
  const [search, setSearch] = useState('');
  const [positionId, setPositionId] = useState<string | undefined>();
  const [orgUnitId, setOrgUnitId] = useState<string | undefined>();
  const [orgUnitName, setOrgUnitName] = useState('');
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedEmployeeId = searchParams.get('employeeId') || null;
  const { data: auth, isLoading: authLoading } = useAuthSession();

  const {
    data: listData,
    isLoading,
    hasNextPage: hasMoreEmployees,
    fetchNextPage: fetchNextEmployees,
    isFetchingNextPage: isFetchingMoreEmployees,
  } = useEmployees({
    q: search || undefined,
    position_id: positionId,
    org_unit_id: orgUnitId,
  });
  const { data: selectedEmpProfile, isLoading: cardLoading } = useEmployee(selectedEmployeeId);
  const {
    data: positionsData,
    hasNextPage: hasMorePositions,
    fetchNextPage: fetchNextPositions,
    isFetchingNextPage: isFetchingMorePositions,
  } = usePositions();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const employees = useMemo(() => listData?.pages.flatMap((page) => page.employees) ?? [], [listData]);

  const positionOptions = useMemo(() => {
    const items = positionsData?.pages.flatMap((page) => page.items) ?? [];
    return [{ value: '', label: 'Все должности' }, ...items.map((p) => ({ value: p.id, label: p.name }))];
  }, [positionsData]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;
    if (!hasMoreEmployees || isFetchingMoreEmployees) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreEmployees && !isFetchingMoreEmployees) {
          void fetchNextEmployees();
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [fetchNextEmployees, hasMoreEmployees, isFetchingMoreEmployees, employees.length]);

  const handlePositionsPopupScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMorePositions || isFetchingMorePositions) return;
      const target = e.currentTarget;
      const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24;
      if (reachedBottom) {
        void fetchNextPositions();
      }
    },
    [fetchNextPositions, hasMorePositions, isFetchingMorePositions],
  );

  const handleBack = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  const handleSelectEmployee = useCallback(
    (id: string) => {
      setSearchParams({ employeeId: id }, { replace: true });
    },
    [setSearchParams],
  );

  const handleOrgSelect = useCallback((unitId: string, unitName: string, opts?: { keepDrawerOpen?: boolean }) => {
    setOrgUnitId(unitId);
    setOrgUnitName(unitName);
    if (!opts?.keepDrawerOpen) {
      setOrgDrawerOpen(false);
    }
  }, []);

  const clearOrgFilter = useCallback(() => {
    setOrgUnitId(undefined);
    setOrgUnitName('');
  }, []);

  if (!selectedEmployeeId && authLoading) {
    return <PageSpinner />;
  }

  if (!authLoading && auth?.user.role === 'employee') {
    const ownId = auth.user.employee_id;
    if (!ownId) {
      return <Empty description="К учётной записи не привязан сотрудник — список недоступен." />;
    }
    if (!selectedEmployeeId || selectedEmployeeId !== ownId) {
      return <Navigate to={`/employees?employeeId=${encodeURIComponent(ownId)}`} replace />;
    }
  }

  if (selectedEmployeeId) {
    if (authLoading || cardLoading) return <PageSpinner />;
    if (!selectedEmpProfile) {
      return (
        <div>
          <Button type="link" className={s['root__back-btn']} onClick={handleBack}>
            ← К списку сотрудников
          </Button>
          <Empty description="Сотрудник не найден" />
        </div>
      );
    }
    return <EmployeeCard profile={selectedEmpProfile} onBack={handleBack} />;
  }

  return (
    <>
      <Space wrap className={s.root__filters}>
        <SearchInput
          className={s['root__filter-search']}
          placeholder="Поиск по ФИО..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
        <Select
          className={s['root__filter-select']}
          showSearch
          optionFilterProp="label"
          value={positionId ?? ''}
          onChange={(v) => setPositionId(v || undefined)}
          options={positionOptions}
          placeholder="Должность"
          onPopupScroll={handlePositionsPopupScroll}
          notFoundContent={isFetchingMorePositions ? <Spin size="small" /> : undefined}
        />
        <Button
          icon={<ApartmentOutlined size={16} />}
          onClick={() => setOrgDrawerOpen(true)}
          className={s['root__org-btn']}
        >
          Оргструктура
        </Button>
      </Space>

      {orgUnitName ? (
        <div className={s['root__active-filter']}>
          <Text type="secondary" className={s['root__filter-label']}>
            Подразделение:
          </Text>
          <Tag closable onClose={clearOrgFilter} className={s['root__filter-tag']}>
            {orgUnitName}
          </Tag>
        </div>
      ) : null}

      {isLoading ? (
        <PageSpinner size="default" />
      ) : employees.length > 0 ? (
        <>
          <div className={s.root__list}>
            {employees.map((emp) => (
              <EmployeeRow key={emp.id} emp={emp} onClick={() => handleSelectEmployee(emp.id)} />
            ))}
          </div>
          <div ref={loadMoreRef} className={s['root__load-more']}>
            {isFetchingMoreEmployees ? <Spin size="small" /> : null}
          </div>
        </>
      ) : (
        <Empty description="Сотрудники не найдены" />
      )}

      <OrgStructureSelectDrawer open={orgDrawerOpen} onClose={() => setOrgDrawerOpen(false)} width={420}>
        <OrgTreeDrawer onSelect={handleOrgSelect} selectedOrgUnitId={orgUnitId} />
      </OrgStructureSelectDrawer>
    </>
  );
}
